import React, { useRef, useEffect, useCallback } from 'react';
import { useFileBrowserStore } from '@/hooks';
import { droppedItemsToFiles } from '@/services/saveService';
import { logger } from '@/utils/logger';
import { resolveDropFolder } from '@/utils/tree';
import { BlobItem } from '@/types';

// Resolve the nearest enclosing folder for a point in the task pane. Reads the
// row's data-* attributes (set by FileList) rather than React state, so it works
// from inside the Office drag event. Returns '' (site root) when no row is hit.
function folderAtPoint(x: number, y: number): string {
  const el = document.elementFromPoint(x, y) as HTMLElement | null;
  const row = el?.closest('[data-path]') as HTMLElement | null;
  if (!row) return '';
  const item = {
    path: row.getAttribute('data-path') || '',
    isFolder: row.getAttribute('data-isfolder') === '1',
  } as BlobItem;
  return resolveDropFolder(item);
}

// The folder row currently highlighted as the drop target, tracked outside React
// because the virtualized DetailsList memoizes rows and will not repaint on a
// dragOverPath change alone. We toggle a class directly on the row element.
let highlightedRow: Element | null = null;
// The folder the drop will land in, tracked outside the store so dragover does
// not write to the store on every move (which re-rendered the whole list).
let currentDropTarget: string | null = null;

// A single drop can reach BOTH the Office DragAndDropEvent handler and the DOM
// drop handler. Observed in new Outlook when dropping an email into empty root
// space: the Office broker handles drops onto real content (folder rows), but a
// drop in the empty area also leaks a native HTML5 drop to the DOM handler, so
// the same email uploaded twice. Whichever handler reaches the drop first claims
// it; the other sees the recent claim and skips. The window is short so two
// genuinely separate drops (always far more than this apart) are never merged.
let lastDropClaimedAt = 0;
function claimDrop(): boolean {
  const now = Date.now();
  if (now - lastDropClaimedAt < 600) return false;
  lastDropClaimedAt = now;
  return true;
}
function setDragHighlight(folderPath: string | null): void {
  if (highlightedRow && highlightedRow.getAttribute('data-path') !== folderPath) {
    highlightedRow.classList.remove('drag-folder-target');
    highlightedRow = null;
  }
  // No row to highlight for the site root (no row has its path) or an empty target.
  if (!folderPath) return;
  if (highlightedRow) return;
  for (const el of Array.from(document.querySelectorAll('[data-path]'))) {
    if (el.getAttribute('data-path') === folderPath) {
      el.classList.add('drag-folder-target');
      highlightedRow = el;
      break;
    }
  }
}

interface DropZoneProps {
  children: React.ReactNode;
}

// ---------------------------------------------------------------------------
// Office DragAndDropEvent registration - module-level SINGLETON.
//
// Office.js handlers cannot be reliably removed, and DropZone unmounts and
// remounts on every home/site navigation. Registering per mount accumulated
// handlers, and each stale handler uploaded a fresh copy of every dropped
// email to whatever folder its closure last saw (the "3 emails -> 9 files,
// extras in the root" bug). Register exactly once per taskpane lifetime and
// read EVERYTHING live from the store singleton at event time.
// ---------------------------------------------------------------------------
let officeDnDRegistered = false;
let officeDragTimer: ReturnType<typeof setTimeout> | null = null;

function clearOfficeDragOverlay(): void {
  const store = useFileBrowserStore.getState();
  store.setIsDragging(false);
  setDragHighlight(null);
  currentDropTarget = null;
}

function registerOfficeDropHandler(): void {
  if (officeDnDRegistered) return;
  if (typeof Office === 'undefined' || !Office.context?.mailbox) return;

  const mailbox = Office.context.mailbox as any;
  if (typeof mailbox.addHandlerAsync !== 'function') return;

  // The DragAndDropEvent may not exist in all hosts; guard gracefully.
  const eventType = (Office.EventType as any).DragAndDropEvent;
  if (!eventType) {
    logger.warn('DropZone: Office.EventType.DragAndDropEvent not available in this host');
    return;
  }

  officeDnDRegistered = true;

  mailbox.addHandlerAsync(
    eventType,
    async (eventData: any) => {
      const data = eventData?.dragAndDropEventData;
      if (!data) {
        return;
      }
      const store = useFileBrowserStore.getState();

      // Show the drop overlay while a message is dragged over the pane. Office
      // emits no "drag-leave", so clear it shortly after the last dragover.
      // dragOverPath is left to the folder-row hover handlers; the overlay
      // falls back to the current folder on its own.
      if (data.type === 'dragover') {
        // Set isDragging once (not per move): a store write per dragover
        // re-renders subscribers and is what made the drop feel laggy.
        if (!store.isDragging) store.setIsDragging(true);
        const x = data.taskPaneX;
        const y = data.taskPaneY;
        if (typeof x === 'number' && typeof y === 'number') {
          // Track the target and highlight imperatively, no store write per move.
          // The drop handler reads currentDropTarget.
          currentDropTarget = folderAtPoint(x, y) || null;
          setDragHighlight(currentDropTarget);
        }
        if (officeDragTimer) clearTimeout(officeDragTimer);
        officeDragTimer = setTimeout(clearOfficeDragOverlay, 400);
        return;
      }

      if (data.type !== 'drop') {
        return;
      }

      // Drop landed: clear the overlay and the pending dragover timer. Read
      // the target BEFORE clearing (hovered folder wins, else open folder).
      const targetPath =
        typeof data.taskPaneX === 'number' && typeof data.taskPaneY === 'number'
          ? folderAtPoint(data.taskPaneX, data.taskPaneY)
          : (currentDropTarget ?? store.currentPath);
      if (officeDragTimer) clearTimeout(officeDragTimer);
      clearOfficeDragOverlay();

      const dropped: Array<{ fileContent: Blob; name: string; type?: string }> =
        data.dataTransfer?.files || [];
      if (dropped.length === 0) {
        return;
      }

      if (!claimDrop()) {
        logger.debug('DropZone: this drop was already handled (Office), skipping');
        return;
      }

      logger.debug(
        'DropZone: Office drop received,',
        dropped.length,
        'item(s), target:',
        targetPath || 'root',
      );

      // Route through the same store action as desktop-file drops so dropped
      // emails surface in the upload-progress tray (visible feedback) and the
      // listing refreshes. Dropped items become named .eml File objects.
      try {
        await store.uploadFiles(droppedItemsToFiles(dropped), targetPath);
      } catch (error) {
        logger.error('DropZone: Office drag-and-drop save failed:', error);
      }
    },
    (result: Office.AsyncResult<void>) => {
      if (result.status === Office.AsyncResultStatus.Succeeded) {
        logger.debug('DropZone: Office DragAndDropEvent handler registered');
      } else {
        // Allow a later mount to retry if registration itself failed.
        officeDnDRegistered = false;
        logger.warn(
          'DropZone: Could not register DragAndDropEvent handler:',
          result.error?.message,
        );
      }
    },
  );
}

export function DropZone({ children }: DropZoneProps) {
  // Granular selectors: DropZone must not re-render on unrelated store updates.
  const currentPath = useFileBrowserStore((s) => s.currentPath);
  const isDragging = useFileBrowserStore((s) => s.isDragging);
  const setIsDragging = useFileBrowserStore((s) => s.setIsDragging);
  const uploadFiles = useFileBrowserStore((s) => s.uploadFiles);

  const dragCounter = useRef(0);
  const isProcessingDrop = useRef(false);

  const resetDragState = useCallback(() => {
    dragCounter.current = 0;
    setIsDragging(false);
    setDragHighlight(null);
    currentDropTarget = null;
  }, [setIsDragging]);

  // Ensure the module-level Office drop handler is registered (no-op after
  // the first successful registration; survives DropZone remounts).
  useEffect(() => {
    registerOfficeDropHandler();
  }, []);

  // ---------------------------------------------------------------
  // DOM drag-and-drop: desktop file drops only.
  // Email drops now go through the Office DragAndDropEvent above.
  // ---------------------------------------------------------------
  useEffect(() => {
    const handleDocumentDragEnter = (e: DragEvent) => {
      e.preventDefault();
      if (e.dataTransfer?.types.includes('Files')) {
        dragCounter.current++;
        setIsDragging(true);
      }
    };

    const handleDocumentDragLeave = (e: DragEvent) => {
      e.preventDefault();
      dragCounter.current--;
      if (dragCounter.current <= 0) {
        resetDragState();
      }
    };

    const handleDocumentDragOver = (e: DragEvent) => {
      e.preventDefault();
      if (e.dataTransfer) {
        e.dataTransfer.dropEffect = 'copy';
      }
      // Track + highlight the folder under the cursor for desktop-file drags too.
      currentDropTarget = folderAtPoint(e.clientX, e.clientY) || null;
      setDragHighlight(currentDropTarget);
    };

    const handleDocumentDrop = async (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();

      // Prevent double-processing
      if (isProcessingDrop.current) {
        logger.debug('DropZone: Ignoring duplicate drop event');
        return;
      }

      isProcessingDrop.current = true;

      // Capture target path before resetting. If the drop lands on a file row,
      // resolve to its parent folder so items are not dropped "into" files.
      const overRow = (e.target as HTMLElement | null)?.closest(
        '[data-path]',
      ) as HTMLElement | null;
      const targetPath = overRow
        ? folderAtPoint(
            overRow.getBoundingClientRect().left + 1,
            overRow.getBoundingClientRect().top + 1,
          )
        : (currentDropTarget ?? currentPath);

      logger.debug('DropZone: DOM drop event, target:', targetPath || 'root');

      // Reset drag state immediately
      resetDragState();

      // Handle desktop file drops only (not email drops)
      if (e.dataTransfer?.files && e.dataTransfer.files.length > 0) {
        if (!claimDrop()) {
          logger.debug('DropZone: this drop was already handled (Office), DOM skipping');
        } else {
          const files = Array.from(e.dataTransfer.files);
          logger.debug(
            'DropZone: Uploading desktop files to:',
            targetPath || 'root',
            files.map((f) => f.name),
          );
          try {
            await uploadFiles(files, targetPath);
          } catch (error) {
            logger.error('DropZone: Upload failed:', error);
          }
        }
      }

      // Reset processing flag after a short delay
      setTimeout(() => {
        isProcessingDrop.current = false;
      }, 100);
    };

    // Add listeners to document with capture phase
    document.addEventListener('dragenter', handleDocumentDragEnter, true);
    document.addEventListener('dragleave', handleDocumentDragLeave, true);
    document.addEventListener('dragover', handleDocumentDragOver, true);
    document.addEventListener('drop', handleDocumentDrop, true);

    return () => {
      document.removeEventListener('dragenter', handleDocumentDragEnter, true);
      document.removeEventListener('dragleave', handleDocumentDragLeave, true);
      document.removeEventListener('dragover', handleDocumentDragOver, true);
      document.removeEventListener('drop', handleDocumentDrop, true);
    };
  }, [currentPath, resetDragState, setIsDragging, uploadFiles]);

  // Safety timeout: if dragging for more than 30 seconds, reset
  useEffect(() => {
    if (isDragging) {
      const timeout = setTimeout(() => {
        logger.debug('DropZone: Safety timeout, resetting drag state');
        resetDragState();
      }, 30000);
      return () => clearTimeout(timeout);
    }
  }, [isDragging, resetDragState]);

  // No full-pane overlay: the only drop highlight is the folder row under the
  // cursor (handled in FileList via dragOverPath), which avoids the confusing
  // double highlight.
  return <div className="drop-zone">{children}</div>;
}
